import { useSnackbar } from '@components/snackbar';
import { EditHideIcon, EyeIcon, LinkIcon } from '@sketchnote/icons';
import { Switch } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';

import { useFileService } from '../api/useFileServices';
import { useFileStore } from '../store/useFileStore';
const { useState, useCallback, useEffect } = React;

export const PublicShare = ({ publicURL }: { publicURL: string }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const [editPassword, setEditPassword] = useState<'set' | 'reset'>();
  const [isPublic, setIsPublic] = useState(false);
  const { togglePublicView } = useFileService();
  const [openSnackbar] = useSnackbar();

  const fetchFile = useFileStore((s) => s.fetchFile);
  const fileId = currentFile?.id as string;
  useEffect(() => {
    setIsPublic(!!currentFile?.isPublic);
  }, [currentFile?.isPublic]);

  useEffect(() => {
    if (currentFile?.isProtected) {
      setEditPassword('set');
    } else setEditPassword('reset');
    setIsPasswordEnabled(!!currentFile?.isProtected);
  }, [currentFile?.isProtected]);

  const handleChangePublicView = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPublic((s) => !s);

    const response = await togglePublicView(fileId, !!e.target.checked);
    if (response) fetchFile({ id: fileId });
  };

  const handleTogglePasswordEnable = async () => {
    if (isPasswordEnabled) {
      //Post call without password to disable password feature
      const response = await API.post('/files/share/password', {
        id: currentFile?.id,
      });
      if (response.data.success) {
        setPassword('');
        setEditPassword('set');
        fetchFile({ id: fileId });
      }
    }
    setIsPasswordEnabled(!isPasswordEnabled);
  };
  const handleUpdatePublicPassword = async () => {
    const response = await API.post('/files/share/password', {
      id: currentFile?.id,
      password: password,
    });
    if (response.data.success) {
      fetchFile({ id: fileId });
      setPassword('');
      setEditPassword('set');
      openSnackbar({
        text: 'password saved successfully',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  return (
    <div className="px-18 py-10 w-full bg-gray-100 rounded-lg  text-gray-600">
      <div className="flex  items-center justify-between font-medium text-sm ">
        <div className="flex items-center">
          <LinkIcon className="mr-8" /> <span>Public link</span>
          {isPublic && (
            <Tippy
              content="Anyone with this link will be able to view this board"
              className="w-221 h-auto bg-gray-999 tippy text-sm text-center"
              placement="top"
            >
              <button
                onClick={() => {
                  navigator.clipboard.writeText(publicURL);
                  setIsCopied(true);
                  setTimeout(() => {
                    setIsCopied(false);
                  }, 1000);
                }}
                className="text-primary-500 ml-8 cursor-pointer font-medium"
              >
                {isCopied ? 'Copied!' : 'Copy link'}
              </button>
            </Tippy>
          )}
        </div>
        <div className="flex items-center">
          <span className="mr-16">Can view</span>
          <Switch checked={isPublic} onChange={handleChangePublicView} />
        </div>
      </div>
      {isPublic && (
        <div className="mt-4">
          <div className="flex items-center justify-between mt-10">
            <span className="text-xs">Passcode</span>
            <Switch checked={isPasswordEnabled} onChange={handleTogglePasswordEnable} />
          </div>
          {isPasswordEnabled && (
            <div>
              <div className="bg-gray-100 rounded-sm border flex w-full p-8 mt-12">
                <input
                  placeholder="Password*"
                  type={`${showPassword ? 'text' : 'password'}`}
                  className="flex-grow bg-gray-100"
                  disabled={editPassword === 'set'}
                  value={editPassword === 'set' ? '************' : password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                {editPassword === 'set' ? (
                  <button
                    onClick={() => {
                      setPassword('');
                      setEditPassword('reset');
                    }}
                  >
                    <span className="text-blue-500 text-xs">Reset</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowPassword(!showPassword);
                      setTimeout(() => {
                        setShowPassword(false);
                      }, 1000);
                    }}
                  >
                    {showPassword ? (
                      <EyeIcon className="text-gray-600" />
                    ) : (
                      <div>
                        <EditHideIcon wrapperClass="text-gray-600" />
                      </div>
                    )}
                  </button>
                )}
              </div>
              {password && (
                <div className="flex text-sm mt-8 w-full justify-end">
                  <button
                    onClick={() => {
                      if (currentFile?.isProtected) {
                        setEditPassword('set');
                      }
                      setPassword('');
                    }}
                    className="text-blue-500 border border-blue-500 px-16 py-6 rounded-sm mr-8"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePublicPassword}
                    className="bg-blue-500 text-white px-16 py-6 rounded-sm"
                  >
                    Save password
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
